import { useTranslation } from 'react-i18next';
import { DurationBar, HBarList, SplitBar } from '../../components/charts';
import { durationFormatter, moneyFormatter } from './format';
import { Panel, StatsBody } from './Panel';
import type { Stats } from './stats';
import type { Remote } from './useRemote';

interface CardProps {
    remote: Remote<Stats>;
    className?: string;
}

export const TopItemsCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('top.title')} description={t('top.hint')} className={className}>
            <StatsBody remote={remote} skeletonClass="h-40">
                {(stats) => {
                    const money = moneyFormatter(i18n.language, stats.currency);
                    return (
                        <HBarList
                            caption={t('top.title')}
                            labelHeading={t('top.item')}
                            valueLabel={t('top.sold')}
                            items={stats.topItems.map((i) => ({ key: i.menuItemId, label: `${i.name} · ${i.revenue > 0 ? money(i.revenue) : t('top.unpaid')}`, value: i.quantity }))}
                            formatValue={count}
                        />
                    );
                }}
            </StatsBody>
        </Panel>
    );
};

/** Money next to a split, because the bar shows order counts only. */
const MoneyRows = ({ rows }: { rows: Array<{ key: string; label: string; amount: string }> }) => (
    <dl className="mt-4 grid gap-1 text-small">
        {rows.map((r) => (
            <div key={r.key} className="flex justify-between gap-3">
                <dt className="text-muted">{r.label}</dt>
                <dd className="tabular-nums text-strong">{r.amount}</dd>
            </div>
        ))}
    </dl>
);

export const OrderTypeCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const { t: tc } = useTranslation();
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('type.title')} description={t('type.hint')} className={className}>
            <StatsBody remote={remote} skeletonClass="h-24">
                {(stats) => {
                    const money = moneyFormatter(i18n.language, stats.currency);
                    const { table, togo } = stats.orderTypes;
                    return (
                        <>
                            <SplitBar
                                caption={t('type.title')}
                                valueLabel={t('tiles.orders')}
                                parts={[
                                    { key: 'table', label: tc('orderType.table'), value: table.orders },
                                    { key: 'togo', label: tc('orderType.togo'), value: togo.orders },
                                ]}
                                formatValue={count}
                            />
                            <MoneyRows
                                rows={[
                                    { key: 'table', label: `${tc('orderType.table')}: ${t('type.revenue')}`, amount: money(table.revenue) },
                                    { key: 'togo', label: `${tc('orderType.togo')}: ${t('type.revenue')}`, amount: money(togo.revenue) },
                                ]}
                            />
                        </>
                    );
                }}
            </StatsBody>
        </Panel>
    );
};

export const PaymentCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('payment.title')} description={t('payment.hint')} className={className}>
            <StatsBody remote={remote} skeletonClass="h-24">
                {(stats) => {
                    const money = moneyFormatter(i18n.language, stats.currency);
                    const { cash, card, unpaid } = stats.payments;
                    return (
                        <>
                            <SplitBar
                                caption={t('payment.title')}
                                valueLabel={t('tiles.orders')}
                                parts={[
                                    { key: 'cash', label: t('payment.cash'), value: cash.orders },
                                    { key: 'card', label: t('payment.card'), value: card.orders },
                                    { key: 'unpaid', label: t('payment.unpaid'), value: unpaid.orders },
                                ]}
                                formatValue={count}
                            />
                            <MoneyRows
                                rows={[
                                    { key: 'cash', label: t('payment.cash'), amount: money(cash.revenue) },
                                    { key: 'card', label: t('payment.card'), amount: money(card.revenue) },
                                    { key: 'unpaid', label: t('payment.unpaidValue'), amount: money(unpaid.value) },
                                ]}
                            />
                        </>
                    );
                }}
            </StatsBody>
        </Panel>
    );
};

const STATES = ['pending', 'accepted', 'preparing', 'ready'] as const;

export const TimingCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const { t: tc } = useTranslation();
    const duration = durationFormatter(i18n.language);
    return (
        <Panel title={t('timing.title')} description={t('timing.hint')} className={className}>
            <StatsBody remote={remote} skeletonClass="h-24">
                {({ timing }) => {
                    const averages = [
                        { key: 'accept', label: t('timing.accept'), seconds: timing.averageToAccept },
                        { key: 'prepare', label: t('timing.prepare'), seconds: timing.averageToPrepare },
                        { key: 'complete', label: t('timing.complete'), seconds: timing.averageToComplete },
                    ];
                    return (
                        <>
                            <dl className="mb-4 grid grid-cols-3 gap-3">
                                {averages.map((a) => (
                                    <div key={a.key}>
                                        <dt className="text-caption text-muted">{a.label}</dt>
                                        <dd className="font-display text-ticket tabular-nums text-strong">
                                            {a.seconds === null ? '\u2014' : duration(a.seconds)}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                            <DurationBar
                                caption={t('timing.states')}
                                labelHeading={t('timing.state')}
                                durationLabel={t('timing.average')}
                                segments={
                                    timing.sampleSize === 0
                                        ? []
                                        : STATES.map((s) => ({ key: s, label: tc(`status.${s}`), minutes: (timing.averageInState[s] ?? 0) / 60 }))
                                }
                                formatValue={(minutes) => duration(minutes * 60)}
                            />
                            <p className="mt-3 text-caption text-muted">
                                {t('timing.note', { count: timing.sampleSize })}
                                {timing.excluded > 0 && ` ${t('timing.excluded', { count: timing.excluded })}`}
                            </p>
                        </>
                    );
                }}
            </StatsBody>
        </Panel>
    );
};
